import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { MasterInventoryItem, AddEquipmentEntry, QuoteType, ResidentialItemType } from '../types/admin';

export interface MasterInventoryItemInput {
  name: string;
  type: 'Casegoods' | 'Modular Component' | 'Modular Equipment';
  kz: number;
  description?: string;
  notes?: string;
  imageBase64?: string;
  /** Send null to clear. */
  additionalManHours?: number | null;
  addEquipment?: AddEquipmentEntry[];
  quoteType?: QuoteType;
  // Accessorial service fields (commercial only)
  isAccessorial?: boolean;
  accessorialLabel?: string;
  accessorialOriginManHours?: number;
  accessorialDestinationManHours?: number;
  // Residential fields
  photo?: string;
  moveUnits?: number;
  cubicFeet?: number;
  weightInPounds?: number;
  bulky?: boolean;
  requiredDisassembly?: boolean;
  /** Send null to clear the category. */
  residentialType?: ResidentialItemType | null;
  roomId?: string;
  /** Dollars. Send null to clear. Packing items and equipment only. */
  unitCost?: number | null;
  /** Whole days. Send null to clear (item is purchased/consumed). */
  rentalDays?: number | null;
}

export function useMasterInventory() {
  const [items, setItems] = useState<MasterInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: MasterInventoryItem[] }>(
        '/admin/master-inventory',
      );
      setItems(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (input: MasterInventoryItemInput): Promise<MasterInventoryItem> => {
    const res = await api.post<{ success: boolean; data: MasterInventoryItem }>(
      '/admin/master-inventory',
      input,
    );
    setItems(prev =>
      [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)),
    );
    return res.data;
  };

  const updateItem = async (
    id: string,
    input: Partial<MasterInventoryItemInput>,
  ): Promise<MasterInventoryItem> => {
    const res = await api.patch<{ success: boolean; data: MasterInventoryItem }>(
      `/admin/master-inventory/${id}`,
      input,
    );
    setItems(prev =>
      prev.map(item => (item.id === id ? res.data : item)),
    );
    return res.data;
  };

  const deleteItem = async (id: string): Promise<void> => {
    await api.del(`/admin/master-inventory/${id}`);
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const bulkUpload = async (
    csv: string,
  ): Promise<{ created: number; updated: number }> => {
    const res = await api.post<{
      success: boolean;
      data: { created: number; updated: number };
    }>('/admin/master-inventory/bulk-upload', { csv });
    await fetchItems();
    return res.data;
  };

  const downloadJson = async (): Promise<void> => {
    const res = await api.get<{ success: boolean; data: Record<string, unknown>[] }>(
      '/admin/master-inventory/download-json',
    );
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'master-inventory.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkUploadJson = async (
    items: Record<string, unknown>[],
  ): Promise<{ created: number; updated: number }> => {
    const res = await api.post<{
      success: boolean;
      data: { created: number; updated: number };
    }>('/admin/master-inventory/bulk-upload-json', { items });
    await fetchItems();
    return res.data;
  };

  return { items, loading, error, createItem, updateItem, deleteItem, bulkUpload, downloadJson, bulkUploadJson, refetch: fetchItems };
}
